import { useFloatingNodeId, useFloating, autoUpdate, flip, offset, size, useClick, useDismiss, useInteractions } from '@floating-ui/react';
import { setElementVars } from '@vanilla-extract/dynamic';
import React__default from 'react';
import { ANIMATION_DURATION_MS } from './constants.js';
import '../../utils/index.js';
import '../theme/index.js';
import { availableHeight, availableWidth } from './dropdown.css.js';
import { useTheme } from '../theme/use_theme.js';
import { useIsMounted } from '../../utils/is_mounted.js';
import { useControlledState } from '../../utils/controlled_state.js';
import { usePreferredTransitionStatus } from '../../utils/preferred_transition_status.js';

const useDropdown = ({ open: controlledOpen, onToggle, id: propId, align = 'start', anchorRef, kind, size: size$1, disabled, className, }) => {
    const { variables } = useTheme();
    const nodeId = useFloatingNodeId();
    const id = propId ?? nodeId;
    const isMountedInDOM = useIsMounted();
    const [open, setOpen] = useControlledState({
        defaultValue: false,
        value: controlledOpen,
        onChange: (nextOpen) => {
            if (isMountedInDOM()) {
                onToggle?.(nextOpen);
            }
        },
    });
    const data = useFloating({
        nodeId,
        open,
        onOpenChange: setOpen,
        placement: `bottom-${align}`,
        middleware: [
            flip(),
            offset(variables.size.spacing.xsmall),
            size({
                apply({ elements, availableHeight: availableHeight$1, availableWidth: availableWidth$1 }) {
                    setElementVars(elements.floating, {
                        [availableWidth]: `${availableWidth$1}px`,
                        [availableHeight]: `${availableHeight$1}px`,
                    });
                },
                padding: variables.size.spacing.small,
            }),
        ],
        whileElementsMounted: autoUpdate,
    });
    React__default.useEffect(() => {
        if (isMountedInDOM() && anchorRef?.current) {
            data.refs.setPositionReference(anchorRef.current);
        }
    }, [anchorRef, data.refs, isMountedInDOM]);
    const { context } = data;
    const { isMounted, status: transitionStatus } = usePreferredTransitionStatus(context, {
        duration: ANIMATION_DURATION_MS,
    });
    const click = useClick(context);
    const dismiss = useDismiss(context);
    const interactions = useInteractions([click, dismiss]);
    return React__default.useMemo(() => ({
        id,
        nodeId,
        open,
        setOpen,
        kind,
        size: size$1,
        disabled,
        className,
        ...interactions,
        ...data,
        isMounted,
        transitionStatus,
    }), [id, nodeId, open, setOpen, interactions, data, isMounted, transitionStatus, kind, size$1, disabled, className]);
};

export { useDropdown };
